import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

interface User {
  id: string;
  organizationId: string;
  email: string;
  name: string;
  role: string; // 'admin' | 'user'
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UsersState {
  users: User[];
  currentUser: User | null;
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: UsersState = {
  users: [],
  currentUser: null,
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getUsers = createAsyncThunk(
  'users/getUsers',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/users/organization/${organizationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const inviteUser = createAsyncThunk(
  'users/inviteUser',
  async (userData: { email: string; password: string; name: string; organizationId: string; role?: string }, { rejectWithValue }) => {
    try {
      // Call auth/register endpoint to create new user
      const response = await apiRequest('/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
      // Response includes { user, tokens } but we only need the user
      return response.user;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (userId: string, { rejectWithValue }) => {
    try {
      await apiRequest(`/users/${userId}`, {
        method: 'DELETE',
      });
      return userId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| ASYNC ACTION HANDLERS ||============================== //

const asyncActionHandlers = {
  // Get users
  [getUsers.pending.type]: { status: 'loading' as const },
  [getUsers.fulfilled.type]: (state: UsersState, action: any) => {
    // Handle null response (404 - no users found)
    if (!action.payload) {
      state.users = [];
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend returns array directly in response
    const users = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.users || action.payload.users || action.payload || [];

    state.users = users;
    state.status = 'success';
    state.error = '';
  },
  [getUsers.rejected.type]: (state: UsersState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch users';
  },

  // Invite user
  [inviteUser.pending.type]: { status: 'loading' as const },
  [inviteUser.fulfilled.type]: (state: UsersState, action: any) => {
    const newUser = action.payload.response || action.payload;
    state.users.unshift(newUser); // Add to beginning of list
    state.status = 'success';
    state.error = '';
  },
  [inviteUser.rejected.type]: (state: UsersState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to invite user';
  },

  // Delete user
  [deleteUser.pending.type]: { status: 'loading' as const },
  [deleteUser.fulfilled.type]: (state: UsersState, action: any) => {
    const userId = action.payload;
    state.users = state.users.filter((u) => u.id !== userId);
    if (state.currentUser?.id === userId) {
      state.currentUser = null;
    }
    state.status = 'success';
    state.error = '';
  },
  [deleteUser.rejected.type]: (state: UsersState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to delete user';
  },
};

// ==============================|| SLICE ||============================== //

export const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError(state) {
      state.error = '';
    },
    setCurrentUser(state, action) {
      state.currentUser = action.payload;
    },
    clearUsersState(state) {
      state.users = [];
      state.currentUser = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    Object.entries(asyncActionHandlers).forEach(([type, handler]) => {
      builder.addCase(type, (state, action) => {
        if (typeof handler === 'function') {
          handler(state, action);
        } else {
          const options = handler;
          state.status = options.status;
        }
      });
    });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearError, setCurrentUser, clearUsersState } = usersSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectUsers = (state: any) => state.users.users;
export const selectCurrentUser = (state: any) => state.users.currentUser;
export const selectUsersStatus = (state: any) => state.users.status;
export const selectUsersError = (state: any) => state.users.error;

export default usersSlice.reducer;
